import { gql } from "@apollo/client";
import {
  contentScheduleApi,
  contentTypeSchemaApi,
  graphqlInstance,
} from "@platformx/authoring-apis";
import {
  applyPublishStep,
  convertToLowerCase,
  frameDocumentType,
  getBrandNameById,
  getCanonicalPagePath,
  getCurrentLang,
  getSelectedBrand,
  parseBrandList,
  planPublishSteps,
  runPublishSteps,
  ScheduleEdit,
  SchedulePartialFailureError,
  ScheduleValue,
} from "@platformx/utilities";
import { applyContentFieldDefaults } from "./contentFieldDefaults";

// brand_name is a system field on schema-based documents (accepted without the schema declaring
// it), same as on standard content types: keep an existing value; the selected brand only seeds a
// document that has none. secondary_brandname is never touched here — only Share With Brands
// sets it. The legacy brand_name_x_str keeps its old behaviour when a schema still declares it.
const appendSelectedBrandForSchemaField = (contentInstance) => {
  const input = { ...contentInstance };
  const selectedBrand = getSelectedBrand();
  if (Object.hasOwn(input, "brand_name_x_str")) {
    const existing = input.brand_name_x_str;
    input.brand_name_x_str =
      parseBrandList(existing).length > 0 ? existing : `${selectedBrand || ""}`;
  }
  if (selectedBrand || input.brand_name) {
    input.brand_name = input.brand_name || selectedBrand;
  }
  return input;
};

export const findMaxCharLength = (validations, fallback = 1000) => {
  const rawLimit = Array.isArray(validations)
    ? validations.find((v) => v.type === "maxLength")?.value
    : validations?.maxLength?.value;
  const limit = Number(rawLimit);
  return limit > 0 ? limit : fallback;
};

export const handleLength = (valueData, validations, setRestOfChar, restOfChar) => {
  const maxCharLength = findMaxCharLength(validations);
  if (maxCharLength) {
    const lengthOfChar = convertToLowerCase(valueData).length;
    const rest = valueData ? maxCharLength - lengthOfChar : 0;
    setRestOfChar({
      ...restOfChar,
      restOfLength: rest < 0 ? 0 : rest,
      lengthOfState: lengthOfChar,
      reachLimit: maxCharLength <= lengthOfChar ? true : false,
    });
  }
};

const getValidationValue = (obj) => {
  if (obj.type === "pattern") {
    return new RegExp(obj.value);
  } else if (obj.type === "required") {
    return obj.value === false ? false : true;
  } else {
    return obj.value;
  }
};

export const fetchValidations = (validations) => {
  if (validations?.length > 0) {
    return validations.reduce((acc, obj) => {
      return {
        ...acc,
        [obj.type]: {
          value: getValidationValue(obj),
          message: obj.message,
        },
      };
    }, {});
  }
  return {};
};

export const calculateInitialValuesWithData = (initialData, data) => {
  const initialValues: any = {};
  const dataObj = Object.keys(initialData);
  dataObj.forEach((field) => {
    initialValues[field] = data[field];
  });
  return initialValues;
};

export const calculateInitialValues = (fields) => {
  const initialValues: any = {};
  if (fields?.length > 0) {
    fields.forEach((field) => {
      let defaultValue;
      if (field?.type === "boolean") {
        defaultValue = false;
      } else if (field?.type === "repeater") {
        defaultValue = [];
      } else if (field?.type === "button") {
        defaultValue = {};
      } else {
        defaultValue = "";
      }
      initialValues[field.name] = field?.value ?? field?.default_value ?? defaultValue;
    });
  }
  return initialValues;
};

// Collections have no public URL, so they carry no canonical_url (mirrors the preview). For
// content the stored canonical is the source of truth: re-sent as-is until the slug changes,
// then rebuilt from the document's own primary brand (not the dropdown brand).
const withCanonicalUrl = (input, contentType, schema_document_type, stored?) => {
  const canonical_url =
    frameDocumentType(schema_document_type || "contentType") === "collection"
      ? ""
      : stored?.path && stored.slug === input?.slug
        ? stored.path
        : getCanonicalPagePath(
            input?.slug,
            getCurrentLang(),
            contentType,
            getBrandNameById(input?.brand_name || ""),
          );
  return canonical_url ? { ...input, canonical_url } : input;
};

export const createContentType = (contentInstance, contentType, schema_document_type) => {
  const newContent = {
    ...withCanonicalUrl(
      appendSelectedBrandForSchemaField(applyContentFieldDefaults(contentInstance, contentType)),
      contentType,
      schema_document_type,
    ),
    name: contentInstance?.title,
  };
  return contentTypeSchemaApi.saveContent({
    contentType: contentType,
    input: newContent,
    schema_document_type: schema_document_type || "contentType",
  });
};

export const updateContentType = (
  savedData,
  contentInstance,
  contentType,
  schema_document_type,
) => {
  const { name, canonical } = savedData.current;
  const newContent = {
    ...withCanonicalUrl(
      appendSelectedBrandForSchemaField(applyContentFieldDefaults(contentInstance, contentType)),
      contentType,
      schema_document_type,
      canonical,
    ),
    name: name,
  };
  return contentTypeSchemaApi.updateContent({
    contentType: contentType,
    input: newContent,
    schema_document_type: schema_document_type || "contentType",
  });
};

export const publishContentType = (savedData, contentType, schema_document_type) => {
  const { id, name } = savedData.current;
  const requestToSend = {
    document_name: name,
    document_id: id,
    status: "publish",
  };
  return contentTypeSchemaApi.publishContentType({
    contentType: contentType,
    input: requestToSend,
    schema_document_type: schema_document_type || "contentType",
  });
};

/**
 * Publishes a saved schema-based item, honouring a schedule (KIWI-6576). Used instead of
 * `publishContentType` whenever the editor ticked a schedule box; with nothing ticked the caller
 * keeps using `publishContentType`, so the unscheduled request is untouched.
 *
 * Rejects on any failure (unlike `publishContentType`, which swallows errors), and rejects with a
 * `SchedulePartialFailureError` if a later step fails after an earlier one already took effect.
 */
export const publishScheduledContent = (
  savedData,
  contentType,
  schema_document_type,
  schedule: ScheduleValue,
  // True when the item keeps a publish schedule that was already in place: the plain "publish now"
  // step must not run then, or it would take the item live ahead of that schedule.
  skipPublishNow = false,
) => {
  const { id, name } = savedData.current;
  const base = { document_name: name, document_id: id, status: "publish" };
  const steps = planPublishSteps(contentType, schedule).filter(
    (step) => !skipPublishNow || step.isSchedule,
  );
  return runPublishSteps(steps, (step) =>
    contentScheduleApi.publishSchemaContent({
      contentType,
      input: applyPublishStep(base, step),
      schema_document_type: schema_document_type || "contentType",
    }),
  );
};

/**
 * Moves or cancels schedules that already exist on an item, from its edit form. These go through
 * the reschedule / cancel mutations: the backend refuses a second schedule on a side that already
 * has one. Rejects on failure; if a later edit fails after an earlier one took effect, a
 * `SchedulePartialFailureError` says so.
 */
export const applyScheduleEdits = async (
  savedData,
  contentType,
  schema_document_type,
  edits: ScheduleEdit[],
) => {
  const { name } = savedData.current;
  for (let index = 0; index < edits.length; index += 1) {
    const { kind, action, time } = edits[index];
    try {
      // Sequential on purpose: the backend serialises per item.
      // eslint-disable-next-line no-await-in-loop
      await contentScheduleApi.runAction({
        family: "schema",
        action:
          action === "cancel"
            ? kind === "publish"
              ? "cancelPublish"
              : "cancelUnpublish"
            : kind === "publish"
              ? "reschedulePublish"
              : "rescheduleUnpublish",
        variables: {
          contentType,
          schema_document_type: frameDocumentType(schema_document_type || "contentType"),
          input: { document_name: name, ...(time ? { schedule_date_time: time } : {}) },
        },
      });
    } catch (error) {
      if (index === 0) throw error;
      throw new SchedulePartialFailureError(index, error);
    }
  }
};

/**
 * Re-exported here so components in this lib reach the schedule error mapper through the module the
 * tests already mock, instead of importing the whole `@platformx/authoring-apis` barrel.
 */
export { getScheduleErrorText } from "@platformx/authoring-apis";

export const fetchSchema = (contentType) => {
  return contentTypeSchemaApi.getSchemaDynamic({
    filter: contentType,
  });
};

export const fetchSchemaWithData = (contentType, documentId, schema_document_type) => {
  return contentTypeSchemaApi.getSchemaDynamicWithData({
    contentType: contentType,
    name: documentId,
    schema_document_type: frameDocumentType(schema_document_type),
  });
};

export const dateAndTimeValidator = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (start <= end && end >= start) {
    return true;
  } else {
    return false;
  }
};

export const dynamicAPICall = async (query, responseVariable) => {
  try {
    // Ensure the query is a valid DocumentNode
    const documentNode =
      typeof query === "string"
        ? gql`
            ${query}
          `
        : query;
    const { data = {} } = await graphqlInstance.query({
      query: documentNode,
      variables: {},
      fetchPolicy: "no-cache",
    });
    return data[responseVariable] || [];
  } catch (err: any) {
    // ShowToastError(err || "Failed to load dropdown values");
    // eslint-disable-next-line no-console
    console.log("dynamicAPICallError", err);
    return [];
  }
};

export const getYearOptions = (startYear = 1970) => {
  const currentYear = new Date().getFullYear();

  return Array.from({ length: currentYear - startYear + 1 }, (_, index) => {
    const year = currentYear - index;

    return year.toString();
  });
};
